// Checklist de requisitos de password en vivo. Cada regla pasa de "pendiente"
// (neutro, circulo vacio) a "cumplido" (accent + tilde) mientras el usuario
// tipea, sin esperar al submit. Pensado para que sea obvio al toque: el usuario
// ve QUE necesita y cuando ya lo cumplio.
//
// Colores: usamos `accent` (color de marca, definido por esquema claro/oscuro en
// el branding) para "cumplido" y `text-muted` para "pendiente" -> se ve bien en
// ambos temas por construccion. El estado tambien va por icono + texto sr-only,
// no depende solo del color (a11y).

import { useRef } from 'react';
import { evaluatePassword } from '@/lib/password-policy';

// Tilde compartido: circulo tenue + check. Exportado para que otros indicadores
// de estado "OK" del signup (ej: "email disponible") usen exactamente el mismo
// glifo que el checklist y los match hints — consistencia visual.
export function CheckIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="8" fill="currentColor" opacity="0.16" />
      <path
        d="M4.5 8.4l2.2 2.2 4.8-4.8"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Familia de íconos de estado (misma caja/peso que CheckIcon) para la lista de
// estado del email: ✕ error, ⚠ warning, spinner neutro. El color lo da el contenedor.
export function XIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="8" fill="currentColor" opacity="0.16" />
      <path d="M5.4 5.4l5.2 5.2M10.6 5.4l-5.2 5.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
export function WarnIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="8" fill="currentColor" opacity="0.16" />
      <path d="M8 4.4v4M8 11h.01" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
export function Spinner(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 animate-spin" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function PendingIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" opacity="0.55" />
    </svg>
  );
}

// Círculo con "!" para el estado de REGRESIÓN: la regla estaba cumplida y un
// backspace la rompió → rojo (no vuelve a gris) para avisar que se perdió algo.
function RegressedIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.8v3.6M8 10.8h.01" />
    </svg>
  );
}

type RuleState = 'pending' | 'met' | 'regressed';

export default function PasswordChecklist({ password }: { password: string }): JSX.Element {
  const rules = evaluatePassword(password);
  // Memoria por regla, entre renders: 'pending' (nunca se cumplió) → 'met' →
  // 'regressed' (se cumplió y se rompió). Vuelve a 'met' al re-cumplir; reset con
  // el campo vacío. useRef (no state) porque es memoria derivada del historial.
  const mem = useRef<Record<string, RuleState>>({});
  const empty = password.length === 0;
  const states = rules.map((rule) => {
    const prev = mem.current[rule.id] ?? 'pending';
    let st: RuleState;
    if (empty) st = 'pending';
    else if (rule.met) st = 'met';
    else st = prev === 'met' || prev === 'regressed' ? 'regressed' : 'pending';
    mem.current[rule.id] = st;
    return { id: rule.id, label: rule.label, st };
  });
  return (
    <ul aria-label="Requisitos de la contraseña" className="pw-checklist">
      {states.map((r) => (
        <li
          key={r.id}
          className={`flex items-center gap-2 text-xs transition-colors ${
            r.st === 'met'
              ? 'text-success font-medium'
              : r.st === 'regressed'
                ? 'text-error font-medium'
                : 'text-text-muted'
          }`}
        >
          {r.st === 'met' ? <CheckIcon /> : r.st === 'regressed' ? <RegressedIcon /> : <PendingIcon />}
          <span>{r.label}</span>
          <span className="sr-only">
            {r.st === 'met' ? '— cumplido' : r.st === 'regressed' ? '— lo rompiste' : '— falta'}
          </span>
        </li>
      ))}
    </ul>
  );
}

// Indicador de coincidencia para el campo "repetir contraseña". Solo aparece
// cuando el usuario ya empezo a tipear la confirmacion, asi no muestra "no
// coinciden" sobre un campo vacio.
export function PasswordMatchHint({
  password,
  confirm,
}: {
  password: string;
  confirm: string;
}): JSX.Element | null {
  if (confirm.length === 0) return null;
  const matches = password === confirm;
  return (
    <p
      aria-live="polite"
      className={`flex items-center gap-2 text-xs transition-colors ${
        matches ? 'text-accent font-medium' : 'text-error'
      }`}
    >
      {matches ? <CheckIcon /> : <PendingIcon />}
      <span>{matches ? 'Las contraseñas coinciden' : 'Las contraseñas todavía no coinciden'}</span>
    </p>
  );
}

// Indicador live de coincidencia para el campo "repetir email". Igual que el de
// password: aparece recien cuando el usuario empezo a tipear la confirmacion.
// Comparacion case-insensitive + trim (igual que la validacion del submit).
export function EmailMatchHint({
  email,
  confirm,
}: {
  email: string;
  confirm: string;
}): JSX.Element | null {
  if (confirm.length === 0) return null;
  const matches = email.trim().toLowerCase() === confirm.trim().toLowerCase();
  return (
    <p
      aria-live="polite"
      className={`flex items-center gap-2 text-xs transition-colors ${
        matches ? 'text-success font-medium' : 'text-error font-medium'
      }`}
    >
      {matches ? <CheckIcon /> : <XIcon />}
      <span>{matches ? 'Los emails coinciden' : 'Los emails NO coinciden'}</span>
    </p>
  );
}
